import { GoogleGenAI } from "@google/genai";
import { Message as AppMessage } from './types'; // Import our app's Message type
import { modelHasCapability } from './models'; // Import modelHasCapability

// Check if API key is available and not empty
const apiKey = process.env.REACT_APP_GEMINI_API_KEY || '';

if (!apiKey) {
    console.warn('Warning: Gemini API key is not set. Please add it to your .env file as REACT_APP_GEMINI_API_KEY');
}

// Initialize the Google Gemini client using the environment variable
const ai = new GoogleGenAI({ apiKey });

// Default model to use if the provided model doesn't match Google's naming scheme
const DEFAULT_GEMINI_MODEL = 'gemini-2.5-flash-preview-04-17';

// Default thinking budget for Gemini models
const DEFAULT_THINKING_BUDGET = 512;

// Define message content type
interface MessageContent {
    type: string;
    text?: string;
    image_url?: { url: string };
}

/**
 * Maps custom model names to Gemini-compatible model names
 */
const getGeminiModelName = (model: string): string => {
    // Map your custom model names to actual Gemini model names
    const modelMap: Record<string, string> = {
        'gemini-2.5-pro-preview-03-25': 'gemini-2.5-pro-preview-03-25',
        'gemini-pro': 'gemini-2.5-flash-preview-04-17',
        'gemini-flash': 'gemini-2.5-flash-preview-04-17',
    };

    return modelMap[model] || DEFAULT_GEMINI_MODEL;
};

/**
 * Formats messages in the structure expected by Gemini API
 * Extracts system messages for separate handling
 */
const formatMessagesForGemini = (messages: any[]) => {
    // Find system message if present
    const systemMessage = messages.find(msg => msg.role === 'system');

    // Format conversation messages (excluding system messages)
    const formattedMessages = messages
        .filter(msg => msg.role !== 'system')
        .map(msg => ({
            role: msg.role === 'assistant' ? 'model' : 'user',
            parts: [
                {
                    text: typeof msg.content === 'string'
                        ? msg.content
                        : Array.isArray(msg.content)
                            ? msg.content
                                .filter((item: MessageContent) => item.type === 'text')
                                .map((item: MessageContent) => item.text || '')
                                .join("\n\n")
                            : JSON.stringify(msg.content)
                }
            ]
        }));

    return { formattedMessages, systemMessage };
};

/**
 * Sends a message to the specified Google Gemini model and returns a response.
 *
 * @param model The ID of the Google Gemini model to use.
 * @param messages An array of message objects representing the conversation history.
 * @param stream Whether to use streaming API (default: false)
 * @returns The text response from Google Gemini API.
 */
export const sendGeminiMessage = async (
    model: string,
    messages: any[],
    stream = false
) => {
    if (!apiKey) {
        throw new Error(`
            Gemini API key is not configured. Please follow these steps:
            
            1. Get an API key from https://aistudio.google.com/app/apikey
            2. Make sure you have enabled the Gemini API in your Google Cloud project
            3. Add the API key to your .env file as REACT_APP_GEMINI_API_KEY
            4. Restart your application
        `);
    }

    try {
        // Convert model name to Gemini-compatible format
        const geminiModelName = getGeminiModelName(model);

        // Format messages for Gemini API
        const { formattedMessages, systemMessage } = formatMessagesForGemini(messages);

        console.log(`Using Gemini model: ${geminiModelName}`);

        // Prepend system message to contents if it exists
        let contents = formattedMessages;
        if (systemMessage) {
            // Add system message as a user message with a special prefix
            contents = [
                {
                    role: 'user',
                    parts: [
                        {
                            text: `<system>\n${systemMessage.content}\n</system>\n`
                        }
                    ]
                },
                ...formattedMessages
            ];
        }

        // Set configuration
        const config = {
            responseMimeType: 'text/plain',
        };

        if (stream) {
            // Generate content using streaming
            const response = await ai.models.generateContentStream({
                model: geminiModelName,
                contents,
                config
            });

            return response;
        } else {
            // Generate content without streaming
            const result = await ai.models.generateContent({
                model: geminiModelName,
                contents,
                config
            });

            // Extract text using the correct property path
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text || '';

            return {
                content: text,
            };
        }
    } catch (error: any) {
        // More detailed error logging
        console.error('Error calling Google Gemini API:', error);

        if (error.message && error.message.includes('API key')) {
            throw new Error(`
                Gemini API key issue: ${error.message}
                
                Please follow these steps to fix it:
                1. Verify your API key at https://aistudio.google.com/app/apikey
                2. Make sure the API key is active and hasn't been revoked
                3. Enable the Gemini API in your Google Cloud Console: https://console.cloud.google.com/apis/library/generativelanguage.googleapis.com
                4. Update your .env file with the correct API key
                5. Restart your application
            `);
        } else if (error.status === 403) {
            throw new Error(`
                Gemini API access forbidden (403 error). 
                
                Please follow these steps to fix it:
                1. Make sure the Generative Language API is enabled in your Google Cloud project
                2. Visit https://console.cloud.google.com/apis/library/generativelanguage.googleapis.com
                3. Verify that your API key has permission to access the Gemini API
                4. Check if your project has billing enabled (may be required for some models)
                5. Confirm you're using the correct model name (gemini-2.5-flash-preview-04-17 is recommended)
            `);
        }

        throw error;
    }
};

/**
 * Processes the streaming response from Gemini API
 * 
 * @param streamResponse The streaming response from Gemini API
 * @param onChunk Callback function to process each chunk
 * @returns The complete response text
 */
export const processGeminiStream = async (
    streamResponse: any,
    onChunk: (text: string) => void
) => {
    let fullResponse = '';

    try {
        for await (const chunk of streamResponse) {
            const chunkText = chunk.text || '';
            fullResponse += chunkText;
            onChunk(chunkText);
        }
    } catch (error) {
        console.error('Error processing Gemini stream:', error);
        throw error;
    }

    return fullResponse;
};

/**
 * Alternative implementation that uses the structured chat format.
 *
 * @param model The ID of the Google Gemini model to use.
 * @param messages An array of message objects representing the conversation history.
 * @returns The chat response from Google Gemini API.
 */
export const sendGeminiChat = async (
    model: string,
    messages: any[],
    stream = false
) => {
    return sendGeminiMessage(model, messages, stream);
}; 